import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { formatMoney } from '../../base/package/util/money';
import style from './ui-win-card.css?inline';
import '../ui-screen-reader-only/ui-screen-reader-only';

const CName = 'ui-win-card';
export const BIG_WIN = 100 * 100;
export const MEGA_WIN = 1000 * 100;
export const CLUB_1000X_WIN = 10000 * 100;

export const WinSizes = {
  regular: 'regular',
  big: 'big',
  mega: 'mega',
  club1000x: 'club1000x',
} as const;

export type WinSizes = (typeof WinSizes)[keyof typeof WinSizes];
@customElement(CName)
export class UiWinCard extends LitElement {
  static readonly styles = unsafeCSS(style);

  @property({ type: String }) winSize: WinSizes = WinSizes.regular;
  @property({ type: String }) name: string;
  @property({ type: Boolean }) newlyEntered = false;
  @property({ type: Number }) amount: number;
  @property({ type: Number }) multiplier: number;

  @state() _showConfetti = false;

  @query('.win-card__confetti-canvas')
  private readonly _confettiCanvas?: HTMLCanvasElement;

  private _confettiTimerId: ReturnType<typeof setTimeout> | undefined;

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this._confettiTimerId);
  }

  firstUpdated() {
    if (this._getWinSize(this.amount) === WinSizes.club1000x) {
      const animationDuration = 3360;
      const waitUntilNextLoop = 2000;

      const loop = () => {
        if (!this.isConnected) return;
        this._showConfetti = true;
        this._confettiTimerId = setTimeout(() => {
          if (!this.isConnected) return;
          this._showConfetti = false;
          this._confettiTimerId = setTimeout(loop, waitUntilNextLoop);
        }, animationDuration);
      };

      const initialDelay = 1000;
      this._confettiTimerId = setTimeout(loop, initialDelay);
    }
  }

  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('_showConfetti') && this._showConfetti) {
      // @ts-expect-error
      import('canvas-confetti').then(({ default: confetti }) => {
        if (this._confettiCanvas) {
          confetti.create(this._confettiCanvas, { resize: true })({
            ticks: 600,
            gravity: 0.8,
            startVelocity: 20,
            scalar: 0.8,
            origin: { y: 2 },
            colors: ['#FEF4D6', '#D0BEA1', '#FDDE9E', '#C5A162', '#9E7F47'],
            zIndex: 1,
          });
        }
      });
    }
  }

  render() {
    const winSize = this._getWinSize(this.amount);
    const classes = classMap({
      'win-card': true,
      [`win-card--${winSize}`]: true,
      'win-card--new': this.newlyEntered,
    });
    const winSizeLabel = () => {
      if (winSize === WinSizes.club1000x) {
        return I18n.activityLane.game.winSize.club1000x;
      }

      if (winSize === WinSizes.mega) {
        return I18n.activityLane.game.winSize.mega;
      }

      if (winSize === WinSizes.big) {
        return I18n.activityLane.game.winSize.big;
      }

      return '';
    };

    // Skip cents if amount is bigger than 99999
    const formattedAmount = this.amount > 99999 ? formatMoney(this.amount, undefined, true) : formatMoney(this.amount);

    // Hide multiplier if it's bigger than 99999 or amount is bigger than 999999
    const multiplier = this.multiplier <= 99999 && this.amount <= 999999 ? `×${this.multiplier}` : '';

    return html`
      <div class=${classes} data-content=${winSizeLabel()}>
        ${winSizeLabel() ? html`<ui-screen-reader-only>${winSizeLabel()}</ui-screen-reader-only>` : nothing}
        ${winSize === WinSizes.club1000x ? this._renderClub1000xShine() : nothing}
        ${this._showConfetti ? this._renderClub1000xConfetti() : nothing}
        <div class="win-card__amount">${formattedAmount}</div>
        <div class="win-card__multiplier">${multiplier}</div>
        <div class="win-card__name">
          <span class="win-card__rolling-text win-card__rolling-text--first">
            ${window.$app.$t.get(I18n.activityLane.game.justWon)}
          </span>
          <span class="win-card__rolling-text win-card__rolling-text--second"> ${this.name?.toLowerCase()} </span>
        </div>
      </div>
    `;
  }

  private _renderClub1000xShine() {
    return html`
      <div class="win-card__shine-wrapper">
        <div class="win-card__shine"></div>
      </div>
    `;
  }

  private _renderClub1000xConfetti() {
    return html`
      <div class="win-card__confetti-wrapper">
        <canvas class="win-card__confetti-canvas"></canvas>
      </div>
    `;
  }

  private _getWinSize(winAmount: number): WinSizes {
    if (winAmount >= CLUB_1000X_WIN) {
      return WinSizes.club1000x;
    }

    if (winAmount >= MEGA_WIN) {
      return WinSizes.mega;
    }

    if (winAmount >= BIG_WIN) {
      return WinSizes.big;
    }

    return WinSizes.regular;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UiWinCard;
  }
}
